import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import {
    Container,
    FilterBarDropdown,
    FilterBarPlaceholder,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import { Deferred, usePage } from '@inertiajs/react';
import dayjs from 'dayjs';

const yearOptions = Array.from(
    { length: dayjs().year() - 2014 + 1 },
    (_, index) => {
        const year = dayjs().year() - index;
        return { id: year, name: String(year) };
    }
);

const monthOptions = Array.from({ length: 12 }, (_, index) => {
    const name = dayjs().startOf('year').month(index).format('MMMM');
    return {
        id: index + 1,
        name: name.charAt(0).toUpperCase() + name.slice(1),
    };
});

const ResultsOverviewView = () => {
    const { results, page_totals, totals, resellers } = usePage().props;
    const { filters, updateFilters, isSorted } = useFilters([
        'results',
        'page_totals',
        'totals',
    ]);

    return (
        <Container fluid>
            <Segment>
                <SegmentHeader header='LKV resultaten' />
                <SegmentContent>
                    <SearchBarFilter
                        only={['results', 'page_totals', 'totals']}
                    >
                        <Deferred
                            data='resellers'
                            fallback={<FilterBarPlaceholder />}
                        >
                            <FilterBarDropdown
                                label={`Resellers (${filters?.resellers?.length || 0}/${Object.keys(resellers || {})?.length || 0})`}
                                options={Object.entries(resellers || {})?.map(
                                    ([id, name]) => ({ id, name })
                                )}
                                multiple
                                clearable
                                value={filters?.resellers || []}
                                onChange={values =>
                                    updateFilters('resellers', values)
                                }
                            />
                        </Deferred>
                        <FilterBarDropdown
                            label={filters.year}
                            options={yearOptions}
                            value={filters?.year || []}
                            onChange={values => updateFilters('year', values)}
                        />
                        <FilterBarDropdown
                            label={
                                monthOptions.find(
                                    option =>
                                        String(option.id) ===
                                        String(filters?.month)
                                )?.name ?? 'Alle maanden'
                            }
                            clearable
                            options={monthOptions}
                            value={filters?.month || ''}
                            onChange={values => updateFilters('month', values)}
                        />
                    </SearchBarFilter>

                    <Table striped>
                        <TableHeader className='[&_th]:whitespace-normal'>
                            <TableRow>
                                <TableHeaderCell>Klant</TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('new_employees_count')}
                                    onClick={() =>
                                        updateFilters(
                                            'sort',
                                            'new_employees_count'
                                        )
                                    }
                                >
                                    Nieuwe Werknemers
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('dgr_identities_count')}
                                    onClick={() =>
                                        updateFilters(
                                            'sort',
                                            'dgr_identities_count'
                                        )
                                    }
                                >
                                    DGR-controles
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted(
                                        'positive_dgr_identities_count'
                                    )}
                                    onClick={() =>
                                        updateFilters(
                                            'sort',
                                            'positive_dgr_identities_count'
                                        )
                                    }
                                >
                                    Positieve DGR-controles
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted(
                                        'positive_dgr_identities_percent'
                                    )}
                                    onClick={() =>
                                        updateFilters(
                                            'sort',
                                            'positive_dgr_identities_percent'
                                        )
                                    }
                                >
                                    % Positieve DGR-controles
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('questionnaires_count')}
                                    onClick={() =>
                                        updateFilters(
                                            'sort',
                                            'questionnaires_count'
                                        )
                                    }
                                >
                                    Vragenlijsten
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted(
                                        'answered_questionnaires_count'
                                    )}
                                    onClick={() =>
                                        updateFilters(
                                            'sort',
                                            'answered_questionnaires_count'
                                        )
                                    }
                                >
                                    Beantwoorde Vragenlijsten
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted(
                                        'answered_questionnaires_percent'
                                    )}
                                    onClick={() =>
                                        updateFilters(
                                            'sort',
                                            'answered_questionnaires_percent'
                                        )
                                    }
                                >
                                    % Beantwoorde Vragenlijsten
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted(
                                        'positive_questionnaires_count'
                                    )}
                                    onClick={() =>
                                        updateFilters(
                                            'sort',
                                            'positive_questionnaires_count'
                                        )
                                    }
                                >
                                    Positieve Vragenlijsten
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted(
                                        'positive_questionnaires_percent'
                                    )}
                                    onClick={() =>
                                        updateFilters(
                                            'sort',
                                            'positive_questionnaires_percent'
                                        )
                                    }
                                >
                                    % Positieve Vragenlijsten
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted('authorizations_count')}
                                    onClick={() =>
                                        updateFilters(
                                            'sort',
                                            'authorizations_count'
                                        )
                                    }
                                >
                                    Machtigingen
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted(
                                        'signed_authorizations_count'
                                    )}
                                    onClick={() =>
                                        updateFilters(
                                            'sort',
                                            'signed_authorizations_count'
                                        )
                                    }
                                >
                                    Ondertekende Machtigingen
                                </TableHeaderCell>
                                <TableHeaderCell
                                    sorted={isSorted(
                                        'signed_authorizations_percent'
                                    )}
                                    onClick={() =>
                                        updateFilters(
                                            'sort',
                                            'signed_authorizations_percent'
                                        )
                                    }
                                >
                                    % Ondertekende Machtigingen
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {results?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={13} />
                            )}

                            {results?.data?.map(result => (
                                <TableRow key={result.customer.id}>
                                    <TableCell>
                                        {result?.customer?.name}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {result.new_employees_count}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {result.dgr_identities_count}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {result.positive_dgr_identities_count}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {result.positive_dgr_identities_percent}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {result.questionnaires_count}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {result.answered_questionnaires_count}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {result.answered_questionnaires_percent}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {result.positive_questionnaires_count}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {result.positive_questionnaires_percent}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {result.authorizations_count}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {result.signed_authorizations_count}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {result.signed_authorizations_percent}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell>Pagina totaal</TableCell>
                                <TableCell textAlign='right'>
                                    {page_totals?.new_employees_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {page_totals?.dgr_identities_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {page_totals?.positive_dgr_identities_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {
                                        page_totals?.positive_dgr_identities_percent
                                    }
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {page_totals?.questionnaires_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {page_totals?.answered_questionnaires_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {
                                        page_totals?.answered_questionnaires_percent
                                    }
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {page_totals?.positive_questionnaires_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {
                                        page_totals?.positive_questionnaires_percent
                                    }
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {page_totals?.authorizations_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {page_totals?.signed_authorizations_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {page_totals?.signed_authorizations_percent}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Algemeen totaal</TableCell>
                                <TableCell textAlign='right'>
                                    {totals?.new_employees_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {totals?.dgr_identities_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {totals?.positive_dgr_identities_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {totals?.positive_dgr_identities_percent}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {totals?.questionnaires_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {totals?.answered_questionnaires_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {totals?.answered_questionnaires_percent}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {totals?.positive_questionnaires_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {totals?.positive_questionnaires_percent}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {totals?.authorizations_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {totals?.signed_authorizations_count}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {totals?.signed_authorizations_percent}
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    </Table>
                </SegmentContent>

                <SegmentFooter>
                    <CustomPagination
                        meta={results?.meta}
                        only={['results', 'page_totals', 'totals']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default ResultsOverviewView;
